import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { CLOSE_FRIENDS, PROFILE, UPDATE_CLOSE_FRIENDS } from "constants/index"
import { isPublicPage } from "appRedux/baseQuery"
import { useAppSelector } from "appRedux/hooks"
import { updateFollowersTab } from "appRedux/slices/profileTabsSlice"
import { updateUserProfile } from "appRedux/slices/userSlice"
import useFollowers from "hooks/useFollowers"
import { useAuth } from "context/AuthContext"
import CloseFriendIcon from "components/atoms/_icons/CloseFriendIcon"
import UsersList from "components/organisms/UsersList"
import LoginRequiredDialog from "components/molecules/LoginRequiredDialog"
import withRoleBasedPermission from "components/HOCs/withRoleBasedPermission"
import withPremiumPermission from "components/HOCs/withPremiumPermission"

type FollowersListProps = {
  id?: number;
}

export default function FollowersList({ id }: FollowersListProps) {
  const [searchKeyword, setSearchKeyword] = useState<string>("")
  const [showLoginRequiredDialog, setShowLoginRequiredDialog] = useState<boolean>(isPublicPage())

  const userProfile = useAppSelector((state) => state.user.profile)
  const followers = useAppSelector((state) => state.profileTabs.followers)

  const { t } = useTranslation()
  const { user } = useAuth()
  const { isFetching } = useFollowers(isPublicPage() ? undefined : id)

  const dispatch = useDispatch()

  function handleFollowerRemovedSuccessfully(id: number) {
    const filteredFollowers = followers.filter(follower => follower.id !== id) || []
    setSearchKeyword("")
    dispatch(updateFollowersTab(filteredFollowers))
    // @ts-ignore
    dispatch(updateUserProfile({
      ...userProfile,
      followerCount: filteredFollowers.length
    }))
  }

  function handleSearchUser(searchedValue: string) {
    setSearchKeyword(searchedValue)
  }

  function handleCloseLoginRequiredDialog() {
    setShowLoginRequiredDialog(false)
  }

  return (
    <>
      {id ? <UsersList
        users={followers}
        loading={isFetching}
        defaultSearchValue={searchKeyword}
        showFollowButton={true}
        showRemoveFollowerButton={user?.id == id}
        extraActionButton={<CloseFriendListButtonPermission
          resource={PROFILE}
          action={UPDATE_CLOSE_FRIENDS}
        />}
        onFollowerRemove={handleFollowerRemovedSuccessfully}
        onSearch={handleSearchUser}
      /> : null}
      <LoginRequiredDialog
        open={showLoginRequiredDialog}
        message={t("auth.loginRequiredToViewFollowers")}
        onClose={handleCloseLoginRequiredDialog}
      />
    </>
  )
}

function CloseFriendListButton() {
  const navigate = useNavigate()

  function handleNavigateToCloseFriendsPage() {
    navigate("close-friends")
  }

  return (
    <CloseFriendsButtonWithPremiumPermission
      feature={CLOSE_FRIENDS}
      onClick={handleNavigateToCloseFriendsPage}
    >
      <CloseFriendIcon stroke={"#FFFFFF"} fill={"#FFFFFF"}/>
    </CloseFriendsButtonWithPremiumPermission>
  )
}

const CloseFriendsButton = styled(Stack)(({ theme }) => ({
  width: "40px",
  height: "40px",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  borderRadius: "50%",
  backgroundColor: theme.palette.primary[10],
  cursor: "pointer"
}))

const CloseFriendListButtonPermission = withRoleBasedPermission(CloseFriendListButton)
const CloseFriendsButtonWithPremiumPermission = withPremiumPermission(CloseFriendsButton)
